import i18next from 'i18next';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { updateSegment } from 'common/api/v1/segmentsLayout';
import {
    SEGMENT_COLOR,
    SEGMENT_STROKE_COLOR,
} from 'common/components/maps/constants';
import { POLYGON_SEGMENT } from 'common/components/segment/constants';
import {
    formatSegment,
    getDictionaryById,
} from 'common/components/segment/helpers';
import { showReponseErrorsAsAlert } from 'common/utils/helpers';
import showToast from 'common/utils/showToast';

import { detectFieldChanges } from '../../multiPanelLayout/helpers';
import * as multiPanelLayoutSelectors from '../../multiPanelLayout/selectors';
import {
    INITIALIZE_VALUES,
    EDIT_SEGMENT,
    EDIT_SEGMENT_FAILURE,
    EDIT_SEGMENT_SUCCESS,
    FETCH_OFFER_PANELS_SUCCESS,
} from '../actionTypes';
import { actions } from '../reducer';
import * as selectors from '../selectors';

import updateSegments from './updateSegments';

const buildSegmentPayload = ({
    commercialOfferId,
    gridConfig,
    presetConfiguration,
    selectedSegment,
    values,
}) => {
    const {
        add_nxt_up,
        algorithm,
        azimuth,
        beam_spacing,
        building_heading,
        building_height,
        category,
        ceil_tilt,
        col_spacing,
        drawing_type,
        fill_algorithm,
        floor_height,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        front_base_height,
        frontal_support,
        gclamp_type,
        has_initial_height,
        height,
        initial_height,
        is_custom_module,
        lfoot_supports_width,
        name,
        orientation,
        outer_rail_length,
        panel,
        parent_field_segment,
        plate_support_length,
        polygon,
        preset_configuration,
        rail_spacing,
        rail_support_length,
        row_spacing,
        safe_zone,
        seam_distance,
        selectedModule,
        sheet_gauge,
        sloof_type,
        snow_load,
        solar_module,
        support_length,
        system_uid,
        system,
        tilt,
        type_application,
        zIndex,
    } = values;

    const ceilZonedGroups = gridConfig.modulesGroups.flat(1) || [];
    const groups = gridConfig.groups || [];
    const solar_modules = gridConfig.modulesGroups.flat(Infinity);

    let assigned = 0;
    let defaultOfferPanelValues = {
        color: SEGMENT_COLOR,
        id: null,
        stroke_color: SEGMENT_STROKE_COLOR,
    };
    let module = null;
    let newPanel = null;

    if (selectedModule) module = selectedModule;
    if (is_custom_module) {
        module = panel;
        newPanel = {
            ...module,
            brand: module.brandName,
            code: module.name,
            max_power: module.watts,
        };
    }

    if (module) {
        defaultOfferPanelValues = {
            ...defaultOfferPanelValues,
            ...module,
        };
    }

    const { color, stroke_color } = defaultOfferPanelValues;
    const { total_area } = selectedSegment;

    const solarCharacterization = {
        assigned,
        azimuth,
        ceil_tilt,
        col_spacing,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        height: height || 0,
        orientation,
        polygon: JSON.stringify(polygon),
        quantity: 0,
        row_spacing,
        safe_zone: parseFloat(safe_zone) || 0,
        tilt,
        total_area,
        zIndex,
    };

    const field_segments = [
        {
            name,
            polygon: polygon.map((point) => ({
                x_coordinate: point[1],
                y_coordinate: point[0],
            })),
            solar_modules: solar_modules.map((module) => ({
                cell: module.cell,
                col: module.col,
                group: module.group,
                is_enable: module.enable,
                row: module.row,
                solar_module_points: JSON.stringify([]),
            })),
        },
    ];

    const solution_fields = {
        beam_spacing,
        front_base_height,
        lfoot_supports_width,
        outer_rail_length,
        plate_support_length,
        rail_spacing,
        rail_support_length,
        seam_distance,
        sheet_gauge,
        support_length,
    };

    const { hasChanges = false } = detectFieldChanges(
        presetConfiguration,
        solution_fields,
    );

    const additionalCustomInputs = () => {
        if (preset_configuration && !hasChanges)
            return { preset_configuration };
        return { ...solution_fields };
    };

    const payload = {
        asce_metadata: ceilZonedGroups,
        commercial_offer: commercialOfferId,
        custom_inputs: {
            add_nxt_up,
            algorithm,
            building_heading,
            building_height,
            category,
            ceil_tilt,
            drawing_type,
            fill_algorithm,
            floor_height,
            frontal_support,
            gclamp_type,
            grid_cols: gridConfig.cols,
            grid_rows: gridConfig.rows,
            grid_selected: gridConfig.selected,
            has_initial_height,
            initial_height,
            panel_orientation: orientation,
            sloof_type,
            snow_load,
            system_uid,
            type_application,
            ...additionalCustomInputs(),
        },
        field_segments,
        groups,
        is_custom_module,
        panel: newPanel,
        parent_field_segment,
        segment_color: color,
        segment_stroke_color: stroke_color,
        system,
        system_uid: system_uid || null,
        ...solarCharacterization,
        quantity: solar_modules.filter((item) => item.enabled).length,
        height: building_height,
    };

    if (!is_custom_module) {
        payload.solar_module = solar_module;
    }

    return payload;
};

export default (config) => (dispatch, getState) => {
    const { callback, commercialOfferId, values } = config;
    const { id: segmentId, parent_field_segment } = values;

    const state = getState();
    const segments = selectors.getSegmentsData(state);
    const segmentsDictionary = getDictionaryById(segments);
    const selectedSegment = segmentsDictionary[segmentId];
    const gridConfig = selectors.getGridConfig(state);
    const presetConfiguration =
        multiPanelLayoutSelectors.getPresetConfiguration(state);

    const payload = buildSegmentPayload({
        commercialOfferId,
        gridConfig,
        presetConfiguration,
        selectedSegment,
        values,
    });

    dispatch(actions[EDIT_SEGMENT]());

    updateSegment(segmentId, payload)
        .then((response) => {
            const segmentData = get(response, 'data', {});
            const formatedSegment = formatSegment(
                segmentData,
                parent_field_segment,
            );
            const updatedSegment = formatedSegment;
            dispatch(actions[EDIT_SEGMENT_SUCCESS](updatedSegment));

            const updatedSegments = segments.map((segment) =>
                segment.id === updatedSegment.id
                    ? { ...segment, ...updatedSegment, open: true }
                    : { ...segment, open: false },
            );
            dispatch(updateSegments(updatedSegments));

            if (segmentData.type === POLYGON_SEGMENT) {
                const initialValues = selectors.getInitialValues(state);

                if (!isEmpty(initialValues)) {
                    dispatch(actions[INITIALIZE_VALUES](updatedSegment));
                }

                const panels = updatedSegments
                    .filter((segment) => segment.type === POLYGON_SEGMENT)
                    .map((segment) => segment.panel);
                dispatch(
                    actions[FETCH_OFFER_PANELS_SUCCESS]({
                        panels,
                    }),
                );
            }

            if (callback) callback();

            showToast({ body: i18next.t('Segment changes have been saved') });
        })
        .catch((error) => {
            dispatch(
                actions[EDIT_SEGMENT_FAILURE](error?.response?.data?.errors),
            );
            showReponseErrorsAsAlert(dispatch, error.response);
        });
};
